
import React, { useState, useMemo } from 'react';
import type { Chef, ChefRequest, ChefRequestType, User } from '../types';
import XIcon from './icons/XIcon';
import ChefHatIcon from './icons/ChefHatIcon';
import { getTranslation } from '../utils/translations';
import ChefAutocomplete from './ChefAutocomplete';
import { getLinkableChefs, isNicknameLockedForUser } from '../utils/chefDisplay';

interface ChefRequestModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User;
    chefs: Chef[];
    chefRequests: ChefRequest[];
    onSubmit: (data: {
        requestType: ChefRequestType;
        targetChefId?: string;
        proposedName?: string;
        proposedEnglishName?: string;
        website?: string;
        message?: string;
    }) => Promise<void>;
}

const ChefRequestModal: React.FC<ChefRequestModalProps> = ({
    isOpen,
    onClose,
    user,
    chefs,
    chefRequests,
    onSubmit,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(user.language || 'en', key);
    const [requestType, setRequestType] = useState<ChefRequestType>('existing');
    const [targetChefId, setTargetChefId] = useState<string | null>(null);
    const [proposedName, setProposedName] = useState('');
    const [proposedEnglishName, setProposedEnglishName] = useState('');
    const [website, setWebsite] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const myRequest = useMemo(
        () => chefRequests.find((r) => r.userId === user.uid && r.status !== 'denied'),
        [chefRequests, user.uid]
    );

    const linkableChefs = useMemo(() => getLinkableChefs(chefs), [chefs]);

    if (!isOpen) return null;

    if (user.chefId) {
        const linkedChef = chefs.find((c) => c.id === user.chefId);
        return (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[500] flex items-center justify-center p-4" onClick={onClose}>
                <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[2rem] p-8 shadow-2xl border dark:border-slate-800" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-3 mb-4">
                        <ChefHatIcon className="w-8 h-8 text-brand-sage" />
                        <h2 className="text-xl font-black text-slate-800 dark:text-slate-100">{t('verifiedChef')}</h2>
                    </div>
                    <p className="text-slate-600 dark:text-slate-400">
                        {linkedChef ? linkedChef.englishName : user.chefId}
                    </p>
                    <button onClick={onClose} className="mt-6 w-full py-3 bg-brand-sage text-white font-black rounded-full text-xs uppercase tracking-widest">
                        {t('close')}
                    </button>
                </div>
            </div>
        );
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (requestType === 'existing' && !targetChefId) {
            setError(t('selectChef'));
            return;
        }
        if (requestType === 'new' && !proposedName.trim() && !proposedEnglishName.trim()) {
            setError(t('chefRequestNameRequired'));
            return;
        }
        if (requestType === 'new') {
            const nicknameCandidates = [proposedEnglishName, proposedName].filter(Boolean);
            if (nicknameCandidates.some((name) => isNicknameLockedForUser(chefs, name, user.uid))) {
                setError(t('chefNicknameLocked'));
                return;
            }
        }
        if (myRequest?.status === 'pending') {
            setError(t('chefRequestPending'));
            return;
        }
        setLoading(true);
        try {
            await onSubmit({
                requestType,
                ...(requestType === 'existing' && targetChefId ? { targetChefId } : {}),
                ...(requestType === 'new' && proposedName.trim() ? { proposedName: proposedName.trim() } : {}),
                ...(requestType === 'new' && proposedEnglishName.trim() ? { proposedEnglishName: proposedEnglishName.trim() } : {}),
                ...(requestType === 'new' && website.trim() ? { website: website.trim() } : {}),
                ...(message.trim() ? { message: message.trim() } : {}),
            });
            onClose();
        } catch (err) {
            console.error(err);
            setError(t('errorSaving'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[500] flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[2rem] shadow-2xl flex flex-col max-h-[90vh] overflow-hidden border dark:border-slate-800" onClick={(e) => e.stopPropagation()}>
                <div className="p-6 border-b dark:border-slate-800 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-2">
                        <ChefHatIcon className="w-6 h-6 text-brand-sage" />
                        <h2 className="text-xl font-black text-slate-800 dark:text-slate-100">{t('requestChefRecognition')}</h2>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-full">
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                {myRequest && (
                    <div className={`mx-6 mt-4 p-3 rounded-xl text-sm font-bold ${
                        myRequest.status === 'pending' ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                        myRequest.status === 'approved' ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                        'bg-slate-50 text-slate-600'
                    }`}>
                        {t('status')}: {t(myRequest.status)}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-3 rounded-xl text-sm font-bold">{error}</div>
                    )}

                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setRequestType('existing')}
                            className={`flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider border transition-all ${
                                requestType === 'existing'
                                    ? 'bg-brand-sage text-white border-brand-sage'
                                    : 'bg-slate-50 dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700'
                            }`}
                        >
                            {t('linkExistingChef')}
                        </button>
                        <button
                            type="button"
                            onClick={() => setRequestType('new')}
                            className={`flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider border transition-all ${
                                requestType === 'new'
                                    ? 'bg-brand-sage text-white border-brand-sage'
                                    : 'bg-slate-50 dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700'
                            }`}
                        >
                            {t('requestNewChef')}
                        </button>
                    </div>

                    {requestType === 'existing' ? (
                        <ChefAutocomplete
                            chefs={linkableChefs}
                            value={targetChefId}
                            onChange={setTargetChefId}
                            currentUserId={user.uid}
                            language={user.language}
                            label={t('selectChef')}
                        />
                    ) : (
                        <>
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('chefName')}</label>
                                <input
                                    value={proposedName}
                                    onChange={(e) => setProposedName(e.target.value)}
                                    className="w-full p-3 border dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('chefEnglishName')}</label>
                                <input
                                    value={proposedEnglishName}
                                    onChange={(e) => setProposedEnglishName(e.target.value)}
                                    className="w-full p-3 border dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('chefWebsite')}</label>
                                <input
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                    placeholder="https://"
                                    className="w-full p-3 border dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm"
                                />
                            </div>
                        </>
                    )}

                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('message')}</label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={3}
                            className="w-full p-3 border dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm resize-none"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading || myRequest?.status === 'pending'}
                        className="w-full py-4 bg-brand-sage text-white font-black rounded-2xl text-xs uppercase tracking-widest disabled:opacity-40"
                    >
                        {loading ? '...' : t('submit')}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ChefRequestModal;
